<template>
	<rmp-sign-in-form-base :title="tc('titles.auth')">
		<v-form :readonly="formSaving || formReadonly" :disabled="formDisabled"
				@submit.prevent="submit"
				ref="form" v-model="formValid">
			<v-row class="pt-2">
				<v-col>
					<rmp-text-field
						autocomplete="username"
						name="username"
						required
						:placeholder="tc('fields.login.placeholder')"
						:label="tc('fields.login.label')"
						:rules="validation.login"
						v-model="user.login">
					</rmp-text-field>
					<rmp-text-field autocomplete="current-password"
									name="password"
									hide-details
									required
									@click:append="isPasswordVisible = !isPasswordVisible"
									:icon="isPasswordVisible ? 'ico_eye' : 'ico_eye-off'"
									:icon-color="colors.secondary.base"
									:type="isPasswordVisible ? 'text' : 'password'"
									:placeholder="tc('fields.password.placeholder')"
									:label="tc('fields.password.label')"
									:rules="validation.password"
									v-model="user.password">
					</rmp-text-field>
				</v-col>
			</v-row>
			<v-row class="mt-5">
				<v-col class="d-flex pt-0 justify-end">
					<rmp-text-btn class="pr-0" :text="tc('buttons.forgotPassword')" :to="isInn(user.login) ? { name: RouteNames.MASTER_ACCOUNT_ACCESS, query: $route.query } : { name: RouteNames.FORGOT_PASSWORD, query: $route.query }">
					</rmp-text-btn>
				</v-col>
			</v-row>
			<v-row class="flex-column">
				<v-col v-if="captchaEnabled" class="pb-2 pt-8">
					<rmp-captcha v-model="captchaToken" :disabled="formSaving || formDisabled || formReadonly"></rmp-captcha>
				</v-col>
				<v-col class="pb-2 pt-2">
					<rmp-btn class="rmp-btn"
							 type="submit"
							 elevation="0"
							 :loading="formSaving"
							 color="primary"
							 :disabled="!formValid || (captchaEnabled && !captchaToken) || formSaving"
							 block>
						{{ tc("buttons.login") }}
					</rmp-btn>
				</v-col>

				<v-col class="d-flex justify-center pt-2 pb-2">
					<rmp-btn
							 :to="{ name: RouteNames.MASTER_ACCOUNT_ACCESS }"
							 block
							 outlined
							 class="rmp-btn">
						{{ tc("buttons.registerOrganization") }}
					</rmp-btn>
				</v-col>
				
				<v-col class="d-flex justify-center pt-2 pb-2">
					<div class="divider">
						<hr>
						<span>или</span>
						<hr>
					</div>
				</v-col>
				
				<v-col class="d-flex justify-center pt-2">
					<rmp-btn outlined
							 block
							 disabled
							 color="#0D4CD3"
							 prepend-icon="ico_government-services"
							 icon-size="28"
							 icon-class="mr-4"
							 class="rmp-btn">
						{{ tc("buttons.publicServicesSignIn") }}
					</rmp-btn>
				</v-col>
			</v-row>
		</v-form>
	</rmp-sign-in-form-base>
</template>

<script setup lang="ts">
import RmpBtn from "@rmp/core/components/buttons/RmpBtn.vue";
import RmpTextField from "@rmp/core/components/fields/RmpTextField.vue";
import { RouteNames } from "@rmp/id/router/routes";
import { requiredRule } from "@rmp/core/utils/validation";
import { validatePersonInn, validateLegalEntityInn } from "@rmp/core/utils/validator";
import RmpSignInFormBase from "@rmp/core/components/form/RmpSignInFormBase.vue";
import { useI18n } from "vue-i18n-composable";
import { useRoute } from "vue-router/composables";
import { useColors } from "@rmp/core/composables/useColors";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useForm } from "@rmp/core/composables/useForm";
import { useSignInStore } from "@rmp/id/stores/signIn";
import RmpTextBtn from "@rmp/core/components/buttons/RmpTextBtn.vue";
import { captchaEnabled } from "@rmp/core/services/captcha";
import RmpCaptcha from "@rmp/core/components/fields/RmpCaptcha.vue";

const { tc } = useI18n();
const route = useRoute();
const { colors } = useColors();

const useStore = useSignInStore;
const store = useStore();
const { save, initialize } = store;
const { user, captchaToken } = storeToRefs(store);

const isPasswordVisible = ref(false);

const validation = {
	login: [requiredRule()],
	password: [requiredRule()]
};

const {
	form,
	formStateLoading,
	formReadonly,
	formDisabled,
	formSaving,
	formValid,
	submit
} = useForm({ useStore }, true);

const isInn = (login) => {
	return validatePersonInn(login) || validateLegalEntityInn(login);
};

initialize();
</script>

<style scoped lang="scss">
.divider {
	width: 100%;
	display: flex;
	align-items: center;
	gap: 12px;
}

.divider hr {
	flex: 1;
	border: none;
	border-top: 1px solid var(--v-grey-darken1);
	margin: 0;
}

.divider span {
	color: var(--v-grey-darken1);
	font-size: 14px;
}
</style>
